import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useGuildsCache, useChannelsCache, clearInstancesCache } from '../hooks/useCache';

interface Guild {
  id: string;
  name: string;
  icon?: string;
}

interface Channel {
  id: string;
  name: string;
}

export const RegisterInstance: React.FC = () => {
  const navigate = useNavigate();
  const { get, post } = useApi();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    instanceId: '',
    instanceName: '',
    region: '',
    guildId: '',
    allowedChannels: [] as string[],
    provider: 'aws',
  });
  
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Use caching hooks
  const guildsCache = useGuildsCache();
  const channelsCache = useChannelsCache(formData.guildId);

  // Derived state from cache
  const guilds = guildsCache.cachedData || [];
  const channels = channelsCache.cachedData || [];
  const loadingChannels = channelsCache.isLoading;

  // Load user's Discord servers on component mount with caching
  useEffect(() => {
    const loadGuilds = async () => {
      try {
        await guildsCache.fetchWithCache(() => get('/guilds'));
      } catch (err) {
        console.error('Failed to load Discord servers:', err);
        setError('Failed to load Discord servers. Please try logging in again.');
      }
    };

    if (user) {
      loadGuilds();
    }
  }, [get, user, guildsCache]);

  // Load channels when guild is selected with caching
  useEffect(() => {
    if (!formData.guildId) {
      return;
    }

    const loadChannels = async () => {
      try {
        await channelsCache.fetchWithCache(() => get(`/channels?guild_id=${formData.guildId}`));
      } catch (err) {
        console.error('Failed to load channels:', err);
        setError('Failed to load channels for selected server.');
      }
    };

    loadChannels();
  }, [formData.guildId, get, channelsCache]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGuildChange = (e: SelectChangeEvent) => {
    setFormData(prev => ({
      ...prev,
      guildId: e.target.value,
      allowedChannels: [] // Reset channels when guild changes
    }));
  };

  const handleChannelChange = (e: SelectChangeEvent<string[]>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      allowedChannels: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!user) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    try {
      await post('/register', {
        guild_id: formData.guildId,
        instance_nickname: formData.instanceName,
        instance_id: formData.instanceId,
        az: formData.region,
        allowed_channels: formData.allowedChannels,
        user_id: user.id,
        username: user.username,
        provider: formData.provider
      });

      // Registration successful - clear instances cache to force refresh
      clearInstancesCache(formData.guildId);
      
      navigate('/instances', { state: { message: 'Instance registered successfully!' } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getChannelName = (channelId: string) => {
    const channel = channels.find((c: Channel) => c.id === channelId);
    return channel ? `#${channel.name}` : channelId;
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, px: 2 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Register New Instance
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Discord Server</InputLabel>
                <Select
                  value={formData.guildId}
                  onChange={handleGuildChange}
                  label="Discord Server"
                >
                  {guilds.map((guild: Guild) => (
                    <MenuItem key={guild.id} value={guild.id}>
                      {guild.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <TextField
                required
                fullWidth
                label="Instance ID"
                name="instanceId"
                value={formData.instanceId}
                onChange={handleChange}
                helperText="The cloud instance ID (e.g., i-1234567890abcdef0)"
              />
            </Grid>
            
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Cloud Provider</InputLabel>
                <Select
                  name="provider"
                  value={formData.provider}
                  onChange={(e) => setFormData(prev => ({ ...prev, provider: e.target.value as string }))}
                  label="Cloud Provider"
                >
                  <MenuItem value="aws">AWS (Amazon Web Services)</MenuItem>
                  <MenuItem value="contabo">Contabo</MenuItem>
                  <MenuItem value="oracle">Oracle Cloud</MenuItem>
                  <MenuItem value="gcp">Google Cloud Platform (GCP)</MenuItem>
                  <MenuItem value="do">Digital Ocean</MenuItem>
                  <MenuItem value="azure">Microsoft Azure</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={12}>
              <TextField
                required
                fullWidth
                label="Instance Name"
                name="instanceName"
                value={formData.instanceName}
                onChange={handleChange}
                helperText="A friendly name for your instance (used in Discord commands)"
              />
            </Grid>
            
            <Grid size={12}>
              <TextField
                required
                fullWidth
                label="Region"
                name="region"
                value={formData.region}
                onChange={handleChange}
                helperText="Region or Zone (e.g., us-east-1)"
              />
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Allowed Channels</InputLabel>
                <Select
                  multiple
                  value={formData.allowedChannels}
                  onChange={handleChannelChange}
                  input={<OutlinedInput label="Allowed Channels" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={getChannelName(value)} size="small" />
                      ))}
                    </Box>
                  )}
                  disabled={!formData.guildId || loadingChannels}
                >
                  {channels.map((channel: Channel) => (
                    <MenuItem key={channel.id} value={channel.id}>
                      #{channel.name}
                    </MenuItem>
                  ))}
                </Select>
                {!formData.guildId && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Select a Discord server first to see available channels
                  </Typography>
                )}
                {formData.guildId && channels.length === 0 && !loadingChannels && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    No text channels found in selected server
                  </Typography>
                )}
                {loadingChannels && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Loading channels...
                  </Typography>
                )}
                {formData.allowedChannels.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Leave empty to allow commands from all channels
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid size={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading || !formData.guildId}
              >
                {loading ? 'Registering...' : 'Register Instance'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}; 